import { EditorPlugin } from 'draft-js-plugins-editor';
import {
  CompositeDecorator,
  ContentBlock,
  ContentState,
  convertFromHTML,
  convertFromRaw,
  convertToRaw,
  EditorState,
  Modifier,
  SelectionState,
} from 'draft-js';
import draftToHTML from 'draftjs-to-html';
import { draftToMarkdown, markdownToDraft } from 'markdown-draft-js';
import { CHECKABLE_LIST_ITEM } from 'draft-js-checkable-list-item';
import createHashtagPlugin from '@draft-js-plugins/hashtag';
import createMarkdownPlugin from './Extensions/draft-js-markdown-shortcuts-plugin';
import {
  createMentionRemarkablePlugin,
  draftMentionsToMarkdownRules,
  insertParsedMentionEntities,
  markdownToDraftMentionEntityRules,
  mentionEditorPlugin,
} from './mention';
import CheckableListItem from './CheckableListItem';
import { MentionSuggestionsProps } from '@draft-js-plugins/mention/lib/MentionSuggestions/MentionSuggestions';
import { Dispatch, MutableRefObject, ReactElement } from 'react';
import createFollowupPlugin, {
  initializeSkipFollowupParsingEntities,
  mdUtils,
} from './FollowupPlugin';
import forgetParsableFollowups from './FollowupPlugin/modifiers/forgetParsableFollowups';
import Occasion from '@repo/utils/helpers/Occasion';
import createLinkifyPlugin from './LinkifyPlugin';
import Hashtag from './Hashtag';
import { initialDefaultReminderTime } from '@repo/redux/reducers/user/metadata';
import { DefaultReminderTime } from '@repo/utils/types';

const baseMarkdownOptions = {
  preserveNewlines: true,
  blockStyles: {
    del_open: 'STRIKETHROUGH',
  },
};

// Options for converting from markdown -> draft-js editor state
const remarkablePlugins = ({ plainText = false }: { plainText?: boolean } = {}) => [
  createMentionRemarkablePlugin(plainText),
  mdUtils.createFollowupRemarkablePlugin,
];
const blockEntitiesRules = {
  ...markdownToDraftMentionEntityRules,
  ...mdUtils.markdownToDraftFollowupEntityRules,
};

// Options for converting draft-js editor state -> markdown
const entityItemRules = {
  ...draftMentionsToMarkdownRules,
  ...mdUtils.draftFollowupsToMarkdownRules,
};

export function buildPlugins({
  readOnly = false,
  followupOccasion,
  setFollowupOccasion,
  setFollowupEntityInteracted,
  delayParsingRef,
  clickableMentions = true,
  disableFollowupParsing = false,
  defaultReminderTime = initialDefaultReminderTime,
}: {
  readOnly?: boolean;
  followupOccasion?: Occasion | null;
  setFollowupOccasion?: Dispatch<Occasion | null>;
  setFollowupEntityInteracted?: Dispatch<boolean>;
  delayParsingRef?: MutableRefObject<boolean>;
  clickableMentions?: boolean;
  disableFollowupParsing?: boolean;
  defaultReminderTime?: DefaultReminderTime;
}): {
  plugins: Array<EditorPlugin>;
  MentionSuggestions: (props: Partial<MentionSuggestionsProps>) => ReactElement;
} {
  const markdownPlugin = createMarkdownPlugin({
    CheckableComponent: CheckableListItem,
  });
  // TODO: what are the repercussions of deleting this? This allows tab completion to work properly
  // on mentions. We might want to jury rig something else together if this disrupts markdown editing
  // expectations?
  delete markdownPlugin.onTab;

  const mentionPlugin = mentionEditorPlugin({ clickableMentions });
  const { MentionSuggestions } = mentionPlugin;
  const plugins = [
    !disableFollowupParsing &&
      createFollowupPlugin(
        followupOccasion,
        setFollowupOccasion,
        setFollowupEntityInteracted,
        delayParsingRef,
        readOnly,
        defaultReminderTime,
      ),
    mentionPlugin,
    markdownPlugin,
    createLinkifyPlugin(),
    createHashtagPlugin({ hashtagComponent: Hashtag }),
  ].filter(Boolean);

  return { plugins, MentionSuggestions };
}

function compositeDecoratorFromPlugins(plugins?: Array<EditorPlugin>) {
  return plugins
    ? new CompositeDecorator(
        plugins
          .filter((plugin) => plugin.decorators)
          .map((plugin) => plugin.decorators)
          .flat(),
      )
    : null;
}

export function markdownToPlainText(md) {
  const draft = markdownToDraft(md, {
    ...baseMarkdownOptions,
    remarkablePlugins: remarkablePlugins({ plainText: true }),
    blockEntities: { ...blockEntitiesRules },
  });

  return (draft.blocks || []).map((block) => block.text).join(' ');
}

export function markdownToEditorState(
  textContent = '',
  plugins?: Array<EditorPlugin>,
): EditorState {
  const markdown = markdownToDraft(textContent, {
    ...baseMarkdownOptions,
    remarkablePlugins: remarkablePlugins(),
    blockEntities: { ...blockEntitiesRules },
  });

  for (const block of markdown.blocks) {
    if (block.type !== 'unordered-list-item') {
      continue;
    }

    if (!block.text.startsWith('[ ]') && !block.text.startsWith('[X]')) {
      continue;
    }

    block.type = CHECKABLE_LIST_ITEM;
    block.data = { checked: block.text.startsWith('[X]') };

    if (block.data.checked) {
      block.text = block.text.replace('[X]', '').trim();
    } else {
      block.text = block.text.replace('[ ]', '').trim();
    }
  }

  const editorState = EditorState.createWithContent(
    convertFromRaw(markdown),
    compositeDecoratorFromPlugins(plugins),
  );

  // initializing these in the plugin initializer doesn't quite work as expected :(
  const editorStateWithIgnoredFollowups = initializeSkipFollowupParsingEntities(editorState);

  // By default, we want the cursor to be at the end of the content
  // when an editor is focused. We can adapt this if this behavior is
  // unexpected.
  return EditorState.moveSelectionToEnd(editorStateWithIgnoredFollowups);
}

export function editorStateToMarkdown(editorState: EditorState): string {
  return draftToMarkdown(convertToRaw(editorState.getCurrentContent()), {
    entityItems: { ...entityItemRules },
    ...baseMarkdownOptions,
    styleItems: {
      [CHECKABLE_LIST_ITEM]: {
        open: (entity) => `- [${entity.data.checked ? 'X' : ' '}] `,
        close: () => '',
      },
    },
  });
}

export function editorSelectionStateToClipboardContent(editorState: EditorState): {
  html: string;
  text: string;
} {
  let contentState = editorState.getCurrentContent();

  const selectionState = editorState.getSelection();
  const selectionStartKey = selectionState.getStartKey();
  const selectionEndKey = selectionState.getEndKey();
  const selectionStartOffset = selectionState.getStartOffset();
  const selectionEndOffset = selectionState.getEndOffset();

  const contentFirstBlock = contentState.getFirstBlock();
  const contentLastBlock = contentState.getLastBlock();

  const selectionIsAtStartOfFirstBlock =
    contentFirstBlock.getKey() === selectionStartKey && selectionStartOffset === 0;
  const selectionIsAtEndOfLastBlock =
    contentLastBlock.getKey() === selectionEndKey &&
    selectionEndOffset === contentLastBlock.getLength();

  const startingBlockKeys = contentState
    .getBlocksAsArray()
    .reverse()
    .map((block) => block.getKey());

  if (!selectionIsAtStartOfFirstBlock) {
    let selectionBefore = SelectionState.createEmpty(contentFirstBlock.getKey());
    selectionBefore = selectionBefore.merge({
      focusKey: selectionStartKey,
      focusOffset: selectionStartOffset,
    });

    contentState = Modifier.removeRange(contentState, selectionBefore, 'forward');
  }

  if (!selectionIsAtEndOfLastBlock) {
    // If we've removed a selection already, the new keys on contentState blocks will have updated.
    // Need to map from the old keys to the new keys and adjust the offsets in the selection that needs
    // to be removed from the end of the content.
    const updatedBlockKeys = contentState
      .getBlocksAsArray()
      .reverse()
      .map((block) => block.getKey());
    const oldBlockKeyToNewBlockKeyMap = startingBlockKeys.reduce(
      (oldToNew, key, idx) => ({ ...oldToNew, [key]: updatedBlockKeys[idx] }),
      {},
    );
    const updatedLastBlock = contentState.getLastBlock();
    let selectionAfter = SelectionState.createEmpty(updatedLastBlock.getKey());

    let anchorOffset = selectionEndOffset;
    if (selectionStartKey === selectionEndKey) {
      anchorOffset = anchorOffset - selectionStartOffset;
    }

    selectionAfter = selectionAfter.merge({
      focusKey: updatedLastBlock.getKey(),
      focusOffset: updatedLastBlock.getLength(),
      anchorKey: oldBlockKeyToNewBlockKeyMap[selectionEndKey],
      anchorOffset,
    });

    contentState = Modifier.removeRange(contentState, selectionAfter, 'forward');
  }

  return {
    html: draftToHTML(convertToRaw(contentState)),
    text: contentState.getPlainText(),
  };
}

// Match rich text blocks to plain text blocks, where plain text blocks are classified as
// subsections of plain text blocks (if multiple plain text blocks are sequentially matched in
// a rich text block)
const blocksToSubsectionedBlocks = (
  richContentBlocks: Array<ContentBlock>,
  textBlocksAsText: Array<string>,
): Array<{ block: ContentBlock; subsections: Array<string> }> => {
  return richContentBlocks.reduce((matchedBlocks, richContentBlock) => {
    const richBlockAsText = richContentBlock.getText();
    const subsections = [];

    if (textBlocksAsText[0] == richBlockAsText) {
      textBlocksAsText.splice(0, 1);
    } else {
      while (richBlockAsText.indexOf(textBlocksAsText[0]) > -1) {
        subsections.push(textBlocksAsText[0]);
        textBlocksAsText.splice(0, 1);
      }
    }

    return matchedBlocks.concat([
      {
        block: richContentBlock,
        subsections,
      },
    ]);
  }, []);
};

const findInsertionPoints = (
  subsectionedBlocks: Array<{ block: ContentBlock; subsections: Array<string> }>,
): Array<SelectionState> => {
  return subsectionedBlocks.reduce((insertionPoints, { block, subsections }) => {
    if (subsections.length == 0) {
      return insertionPoints;
    }

    const blockKey = block.getKey();
    let blockText = block.getText();
    let droppedTextLength = 0;

    const whitespaceIdxs = subsections
      .filter((textChunk) => textChunk != '')
      .reduce((idxs, textChunk) => {
        const textChunkIndex = blockText.indexOf(textChunk);
        const idx = textChunkIndex + droppedTextLength;

        blockText = blockText.slice(textChunk.length);
        droppedTextLength += textChunk.length;

        return idxs.concat([idx]);
      }, [])
      .slice(1);

    let whitespaceCharOffset = -1;

    return insertionPoints.concat(
      whitespaceIdxs.map((idx) => {
        whitespaceCharOffset += 1;
        return SelectionState.createEmpty(blockKey).merge({
          focusOffset: idx + whitespaceCharOffset,
          anchorKey: blockKey,
          anchorOffset: idx + whitespaceCharOffset,
        });
      }),
    );
  }, []);
};

const insertNewlines = (html: string, textContentBlocks: Array<ContentBlock>): ContentState => {
  const richBlocks = convertFromHTML(html);
  const textBlocksAsText = textContentBlocks.map((block) => block.getText());
  const subsectionedBlocks = blocksToSubsectionedBlocks(richBlocks.contentBlocks, textBlocksAsText);
  const insertionPoints = findInsertionPoints(subsectionedBlocks);

  return insertionPoints.reduce((contentState, insertionPoint) => {
    return Modifier.replaceText(contentState, insertionPoint, '\n');
  }, ContentState.createFromBlockArray(richBlocks.contentBlocks, richBlocks.entityMap));
};

export const insertPastedContent = (
  text: string,
  html: string | null,
  editorState: EditorState,
): EditorState => {
  // if text is null or undefined, it means that the clipboard is empty
  if (!text) {
    return editorState;
  }

  const textContentBlocks = ContentState.createFromText(text).getBlockMap().valueSeq().toArray();
  const pastedHTML = html ? html : text;
  const pastedContentWithNewlines = insertNewlines(pastedHTML, textContentBlocks);
  const pastedContentStateWithMentions = insertParsedMentionEntities(
    pastedHTML,
    pastedContentWithNewlines,
  );

  // for now we'll default to ignoring any followups that are pasted in
  // const pastedContentWithParsableFollowupsIgnored = forgetParsableFollowups(pastedContentStateWithMentions, )
  const pastedEditorState = EditorState.createWithContent(pastedContentStateWithMentions);
  const editorStateWithIgnoredFollowups = forgetParsableFollowups(pastedEditorState, false);
  const newState = Modifier.replaceWithFragment(
    editorState.getCurrentContent(),
    editorState.getSelection(),
    editorStateWithIgnoredFollowups.getCurrentContent().getBlockMap(),
  );

  return EditorState.push(editorState, newState, 'insert-fragment');
};
